import { Box, Button, Tooltip, styled, useTheme } from "@mui/material";
import { ActivityStatus, Credential } from "generated-graphql/graphql";
import { CredentialVerificationStatus } from "generated-graphql/graphql";
import { VERIFY_CREDENTIALS_DENIED_STATES } from "encamp-shared/src/constants/states";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { relativeLogTime } from "util/dates";
import { useVerifyCredential } from "hooks/useVerifyCredentials";
import { useCreateTierIIReportComment } from "hooks/comments";
import { useActivityLogs } from "hooks/useActivityLogs";
import { useReportDetails } from "./useReportDetails";
import { FieldDisplay } from "./shared/FieldComponents";

const RELATIVE_TIME = 12;
const NEVER_CHECKED = "Never Checked";

export const CredentialVerification = ({
  credential,
  facilityId,
  state,
  disabled = false,
}: {
  credential: Credential | undefined | null;
  facilityId: string;
  state: string | null | undefined;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  const { data } = useReportDetails();
  const [verifyCredential, { loading: sendingCredentialVerification }] =
    useVerifyCredential();
  const [createComment] = useCreateTierIIReportComment();

  // Get current activity and check if it's in error
  const currentActivity = data?.tierIIReport.currentWorkflow?.activities.find(
    (act) => act.id === data?.tierIIReport.currentWorkflow?.currentActivityId
  );
  const isActivityInError = currentActivity?.status === ActivityStatus.Error;

  const isStateDenied = VERIFY_CREDENTIALS_DENIED_STATES.includes(
    state as any
  );

  // Get error message from activity logs if activity is in error
  const { data: logData } = useActivityLogs(
    currentActivity?.id,
    isActivityInError ? `status:${ActivityStatus.Error}` : "",
    0,
    1,
    [{ field: "time", sort: "desc" }]
  );

  const errorMessage =
    isActivityInError && logData?.activityLogs.items?.[0]?.message
      ? logData.activityLogs.items[0].message
      : null;

  const workflowId = data?.tierIIReport.currentWorkflow?.id;
  const reportId = data?.tierIIReport.id;

  const handleVerifyCredential = async () => {
    await verifyCredential({
      variables: {
        input: {
          facilityId: facilityId,
          credentialId: credential?.id,
        },
      },
      refetchQueries: ["GetTierIIReport", "GetCredential"],
    });

    // Add workflow comment
    if (workflowId && reportId) {
      let commentText = "Verified credential";
      if (errorMessage) {
        commentText += `:\n"${errorMessage}"`;
      }

      await createComment({
        variables: {
          input: {
            workflowId,
            reportId,
            comment: commentText,
          },
        },
      });
    }
  };

  const verificationTime = credential?.lastVerificationCheckAt;
  const lastSuccessfulVerificationTime = credential?.lastVerificationSuccessAt;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(1) }}
    >
      <FieldDisplay
        label="Verification Status:"
        value={prettyPrintEnumValue(credential?.verificationStatus)}
        noWrap
      />
      <FieldDisplay
        label="Last Check:"
        value={
          <Tooltip title={verificationTime}>
            <span>
              {verificationTime
                ? relativeLogTime(verificationTime, RELATIVE_TIME)
                : NEVER_CHECKED}
            </span>
          </Tooltip>
        }
        noWrap
      />
      <FieldDisplay
        label={
          <>
            Last Successful
            <br />
            Verification Check:
          </>
        }
        value={
          <Tooltip title={lastSuccessfulVerificationTime}>
            <span>
              {lastSuccessfulVerificationTime
                ? relativeLogTime(lastSuccessfulVerificationTime, RELATIVE_TIME)
                : NEVER_CHECKED}
            </span>
          </Tooltip>
        }
        alignItems="flex-start"
        noWrap
      />
      <Box paddingTop={theme.spacing(1)}>
        <Tooltip
          title={
            isStateDenied
              ? "Credential verification is not supported for this state"
              : ""
          }
        >
          <span>
            <Button
              disabled={
                disabled ||
                isStateDenied ||
                credential?.verificationStatus ===
                  CredentialVerificationStatus.CurrentlyVerifying ||
                !!sendingCredentialVerification
              }
              variant="outlined"
              onClick={handleVerifyCredential}
              size="small"
            >
              Verify Credential
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};
